// ── CompetencyTab.js ────────────────────────────────────────────────────────
// V2 — Distinct destination styling. No emojis. Strong terracotta presence.
// Incremental "step-through" solution reveal (one teaching block at a time).
// Drill-down navigation: Subjects → Chapters → Types → Questions → Detail.

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  loadCompetencyManifest,
  loadCompetencyChapter,
} from "../competency";

// ── Constants ──
const SUBJECTS = ["Maths", "Science", "Geography", "Economics"];
const SUBJECT_INITIAL = { Maths: "M", Science: "S", Geography: "G", Economics: "E" };
const SUBJECT_COLORS = {
  Maths: { bg: "#FAEEDA", c: "#854F0B" },
  Science: { bg: "#E8F2F8", c: "#085041" },
  Geography: { bg: "#EAF3DE", c: "#27500A" },
  Economics: { bg: "#EEEDFE", c: "#3C3489" },
};
const TYPE_SHORT = {
  "case-study": "CS",
  "assertion-reason": "AR",
  "application-mcq": "AP",
};

// COMPETENCY-TERRACOTTA-V2: stronger, more visible palette
const TERRACOTTA = {
  primary: "#B5491C",        // deeper than v1 — visible against page bg
  primaryDark: "#7C2D12",
  pageBg: "#FBEFE6",         // warm terracotta wash for the whole section
  cardBg: "#FFFFFF",
  light: "#FEF0E2",
  border: "rgba(181,73,28,0.20)",
  tint: "rgba(181,73,28,0.10)",
  heroStart: "#B5491C",
  heroEnd: "#E8946E",
};

const ACCENT = {
  terracotta: { bg: "rgba(181,73,28,0.10)", border: "#B5491C", labelColor: "#B5491C" },
  green:      { bg: "rgba(26,122,82,0.10)", border: "#1A7A52", labelColor: "#1A7A52" },
  blue:       { bg: "rgba(46,158,206,0.10)", border: "#2E9ECE", labelColor: "#2E9ECE" },
  marigold:   { bg: "rgba(232,168,23,0.14)", border: "#B07A0F", labelColor: "#8A5F0B" },
};

// ── Style primitives ──
const fadeUp = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.2 },
};

const cardStyle = {
  background: TERRACOTTA.cardBg,
  borderRadius: 14,
  padding: "14px 16px",
  marginBottom: 10,
  borderTop: "1px solid rgba(15,27,51,0.08)",
  borderRight: "1px solid rgba(15,27,51,0.08)",
  borderBottom: "1px solid rgba(15,27,51,0.08)",
  borderLeft: `4px solid ${TERRACOTTA.primary}`,
  boxShadow: "0 2px 12px rgba(181,73,28,0.08)",
  cursor: "pointer",
};

const cardDisabledStyle = {
  ...cardStyle,
  cursor: "not-allowed",
  opacity: 0.5,
  borderLeft: "4px solid rgba(15,27,51,0.18)",
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.12em",
  color: TERRACOTTA.primary,
  textTransform: "uppercase",
  marginBottom: 6,
};

const backBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 999,
  background: TERRACOTTA.cardBg,
  border: `1px solid ${TERRACOTTA.border}`,
  color: TERRACOTTA.primary,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  marginBottom: 14,
};

const headingStyle = {
  fontSize: 19,
  fontWeight: 800,
  color: "#0F1B33",
  marginBottom: 6,
};

const subTextStyle = { fontSize: 13, color: "#5A6581" };

const continueBtnStyle = {
  marginTop: 6,
  padding: "12px 14px",
  background: TERRACOTTA.primary,
  color: "#FFFFFF",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 700,
  textAlign: "center",
  cursor: "pointer",
  boxShadow: "0 2px 10px rgba(181,73,28,0.30)",
  letterSpacing: "0.02em",
};

// ── Page wrapper: padded content area within the full-page route ──
// Note: outer page already has terracotta pageBg, so this is just inner padding.
function PageWrapper({ children }) {
  return (
    <div style={{ padding: "16px 16px 32px" }}>
      {children}
    </div>
  );
}

// ── Hero banner (Subjects level only) ──
function CompetencyHero({ isHi }) {
  return (
    <div style={{
      background: `linear-gradient(135deg, ${TERRACOTTA.heroStart}, ${TERRACOTTA.heroEnd})`,
      borderRadius: 16,
      padding: "22px 20px",
      marginBottom: 16,
      boxShadow: "0 8px 22px rgba(181,73,28,0.22)",
    }}>
      <div style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.20em",
        color: "rgba(255,255,255,0.88)",
        textTransform: "uppercase",
        marginBottom: 8,
      }}>
        Competency Building
      </div>
      <div style={{
        fontSize: 22,
        fontWeight: 800,
        color: "#FFFFFF",
        lineHeight: 1.2,
        marginBottom: 6,
        letterSpacing: "-0.01em",
      }}>
        {isHi ? "Board ke saath ek kadam aage" : "Build exam recognition for boards"}
      </div>
      <div style={{
        fontSize: 13,
        color: "rgba(255,255,255,0.88)",
        lineHeight: 1.45,
      }}>
        {isHi
          ? "CBSE 2026 pattern par case studies aur assertion-reason questions."
          : "CBSE 2026 case studies, assertion-reason, and competency MCQs."}
      </div>
    </div>
  );
}

// ── Sub-section heading bar (inner levels — chapters / types / questions) ──
function SectionHeader({ eyebrow, title, isHi }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {eyebrow && (
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: "0.16em",
          color: TERRACOTTA.primary, textTransform: "uppercase", marginBottom: 4,
        }}>
          {eyebrow}
        </div>
      )}
      <div style={headingStyle}>{title}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component — exported as CompetencyPage (full-page route, not a tab).
// Renders its own top header so it visually replaces the home page entirely.
// ────────────────────────────────────────────────────────────────────────────
export function CompetencyPage({ cls, lang, curriculumData, onBack }) {
  const isHi = lang === "hi";
  const [navStack, setNavStack] = useState([{ level: "subjects" }]);
  const [manifest, setManifest] = useState(null);
  const view = navStack[navStack.length - 1];

  useEffect(() => {
    loadCompetencyManifest().then(setManifest);
  }, []);

  const goBack = () => setNavStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  const goTo = (newView) => setNavStack((s) => [...s, newView]);

  // Inner level renderer
  let inner = null;
  if (view.level === "subjects") {
    inner = <SubjectsLevel cls={cls} isHi={isHi} manifest={manifest} goTo={goTo} />;
  } else if (view.level === "chapters") {
    inner = <ChaptersLevel cls={cls} isHi={isHi} view={view} curriculumData={curriculumData} manifest={manifest} goBack={goBack} goTo={goTo} />;
  } else if (view.level === "types") {
    inner = <TypesLevel view={view} isHi={isHi} goBack={goBack} goTo={goTo} />;
  } else if (view.level === "questions") {
    inner = <QuestionsLevel view={view} isHi={isHi} goBack={goBack} goTo={goTo} />;
  } else if (view.level === "detail") {
    inner = <DetailLevel view={view} isHi={isHi} goBack={goBack} />;
  }

  return (
    <div style={{
      minHeight: "calc(100vh / 0.92)", zoom: 0.92,
      background: TERRACOTTA.pageBg,
      fontFamily: '"Poppins", sans-serif',
      color: "#0F1B33",
    }}>
      <PageTopHeader onBack={onBack} isHi={isHi} />
      {inner}
    </div>
  );
}

// Back-compat alias (in case anything still imports the old name).
export const CompetencyTab = CompetencyPage;

// ── Top page header (back arrow + "Competency Building" title) ──
function PageTopHeader({ onBack, isHi }) {
  return (
    <div style={{
      background: "#FFFFFF",
      borderBottom: "1px solid rgba(15,27,51,0.08)",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      position: "sticky",
      top: 0,
      zIndex: 10,
      boxShadow: "0 1px 0 rgba(15,27,51,0.04)",
    }}>
      <button
        onClick={onBack}
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: TERRACOTTA.tint, border: "none",
          color: TERRACOTTA.primary, fontSize: 18, fontWeight: 700,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "inherit",
        }}
        aria-label={isHi ? "Peeche" : "Back"}
      >
        ←
      </button>
      <div style={{
        fontSize: 16, fontWeight: 700, color: "#0F1B33",
        letterSpacing: "-0.01em",
      }}>
        {isHi ? "Competency Building" : "Competency Building"}
      </div>
    </div>
  );
}

// ── Level: Subjects ──
function SubjectsLevel({ cls, isHi, manifest, goTo }) {
  return (
    <PageWrapper>
      <CompetencyHero isHi={isHi} />
      <SectionHeader title={isHi ? "Vishay chuno" : "Pick a subject"} isHi={isHi} />
      <div style={{ ...subTextStyle, marginBottom: 14 }}>
        {isHi
          ? "Sirf woh subjects khulenge jinme competency content abhi available hai."
          : "Subjects with available competency content are open."}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {SUBJECTS.map((subj) => {
          const sc = SUBJECT_COLORS[subj];
          const hasContent = manifest &&
            Object.values(manifest.chapters || {}).some(
              (c) => c.subject === subj && Number(c.class) === Number(cls)
            );
          return (
            <div key={subj}
              onClick={() => hasContent && goTo({ level: "chapters", subject: subj })}
              style={hasContent ? cardStyle : cardDisabledStyle}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: sc.bg, color: sc.c,
                fontSize: 20, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 10,
              }}>
                {SUBJECT_INITIAL[subj]}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: sc.c, marginBottom: 3 }}>
                {subj}
              </div>
              <div style={{ fontSize: 11, color: "#5A6581" }}>
                {hasContent ? (isHi ? "Khulo →" : "Open →") : (isHi ? "Jald" : "Coming soon")}
              </div>
            </div>
          );
        })}
      </div>
    </PageWrapper>
  );
}

// ── Level: Chapters ──
function ChaptersLevel({ cls, isHi, view, curriculumData, manifest, goBack, goTo }) {
  const subj = view.subject;
  const sc = SUBJECT_COLORS[subj];
  const allChapters = curriculumData?.CBSE?.[String(cls)]?.[subj]?.chapters || [];
  const availableKeys = new Set(
    Object.entries(manifest?.chapters || {})
      .filter(([k, m]) => m.subject === subj && Number(m.class) === Number(cls))
      .map(([k]) => k)
  );

  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <SectionHeader eyebrow={isHi ? "Competency · " + subj : "Competency · " + subj} title={isHi ? "Adhyay chuno" : "Pick a chapter"} isHi={isHi} />
      <div style={{ ...subTextStyle, marginBottom: 14 }}>
        {isHi
          ? "Competency content wale chapters open hain."
          : "Chapters with competency content are open."}
      </div>
      {allChapters.map((ch, i) => {
        const chapterKey = `class-${cls}-${subj.toLowerCase()}-ch${ch.ncert_chapter}-${slugify(ch.name)}`;
        const hasContent = availableKeys.has(chapterKey);
        const meta = hasContent ? manifest.chapters[chapterKey] : null;
        return (
          <div key={i}
            onClick={() => hasContent && goTo({
              level: "types", subject: subj, chapterKey,
              chapterName: ch.name, ncert: ch.ncert_chapter,
            })}
            style={hasContent ? cardStyle : cardDisabledStyle}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: sc.bg, color: sc.c,
                fontSize: 13, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                Ch {ch.ncert_chapter}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B33", marginBottom: 2 }}>
                  {ch.name}
                </div>
                <div style={{ fontSize: 11, color: "#5A6581" }}>
                  {hasContent
                    ? `${meta.questionCount} ${isHi ? "competency prashn available" : "competency questions available"}`
                    : (isHi ? "Jald aa raha hai" : "Coming soon")}
                </div>
              </div>
              {hasContent && (
                <div style={{ color: TERRACOTTA.primary, fontSize: 18, fontWeight: 700 }}>→</div>
              )}
            </div>
          </div>
        );
      })}
    </PageWrapper>
  );
}

// ── Level: Types ──
function TypesLevel({ view, isHi, goBack, goTo }) {
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadCompetencyChapter(view.chapterKey).then((d) => {
      setChapter(d);
      setLoading(false);
    });
  }, [view.chapterKey]);

  if (loading) return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <div style={{ ...subTextStyle, padding: 20 }}>{isHi ? "Load ho raha…" : "Loading…"}</div>
    </PageWrapper>
  );
  if (!chapter) return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <div style={{ ...subTextStyle, padding: 20 }}>{isHi ? "Content nahi mila" : "No content available"}</div>
    </PageWrapper>
  );

  const byType = {};
  for (const q of chapter.competencyQuestions || []) {
    if (!byType[q.type]) byType[q.type] = { typeLabel: q.typeLabel, section: q.section, qs: [] };
    byType[q.type].qs.push(q);
  }

  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <SectionHeader eyebrow={isHi ? "Competency" : "Competency"} title={chapter.chapter} isHi={isHi} />
      <div style={{ ...subTextStyle, marginBottom: 14 }}>
        {isHi ? "Prashn ka prakar chuno" : "Pick a question type"}
      </div>
      {Object.entries(byType).map(([type, info]) => (
        <div key={type}
          onClick={() => goTo({
            level: "questions", chapterKey: view.chapterKey,
            chapterName: chapter.chapter, type,
            typeLabel: info.typeLabel, section: info.section, questions: info.qs,
          })}
          style={cardStyle}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: TERRACOTTA.tint, color: TERRACOTTA.primary,
              fontSize: 13, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {TYPE_SHORT[type] || "Q"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B33", marginBottom: 2 }}>
                {info.typeLabel}
              </div>
              <div style={{ fontSize: 11, color: "#5A6581" }}>
                {info.section} · {info.qs.length} {info.qs.length === 1 ? (isHi ? "prashn" : "question") : (isHi ? "prashn" : "questions")}
              </div>
            </div>
            <div style={{ color: TERRACOTTA.primary, fontSize: 18 }}>→</div>
          </div>
        </div>
      ))}
    </PageWrapper>
  );
}

// ── Level: Questions list ──
function QuestionsLevel({ view, isHi, goBack, goTo }) {
  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <SectionHeader eyebrow={`${view.chapterName} · ${view.section}`} title={view.typeLabel} isHi={isHi} />
      {view.questions.map((q) => (
        <div key={q.id}
          onClick={() => goTo({ level: "detail", chapterName: view.chapterName, question: q })}
          style={cardStyle}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B33", marginBottom: 4 }}>
            {q.title}
          </div>
          <div style={{ fontSize: 11, color: "#5A6581" }}>
            {q.marks} {q.marks === 1 ? (isHi ? "ank" : "mark") : (isHi ? "ank" : "marks")}
            {q.type === "case-study" && q.subQuestions
              ? ` · ${q.subQuestions.length} ${isHi ? "up-prashn" : "sub-questions"}`
              : ""}
          </div>
        </div>
      ))}
    </PageWrapper>
  );
}

// ── Level: Detail dispatcher ──
function DetailLevel({ view, isHi, goBack }) {
  const q = view.question;
  if (q.type === "case-study") return <CaseStudyDetail q={q} isHi={isHi} goBack={goBack} />;
  if (q.type === "assertion-reason") return <AssertionReasonDetail q={q} isHi={isHi} goBack={goBack} />;
  if (q.type === "application-mcq") return <ApplicationMcqDetail q={q} isHi={isHi} goBack={goBack} />;
  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <div style={{ ...subTextStyle, padding: 20 }}>{isHi ? "Renderer nahi mila" : "No renderer for this type"}</div>
    </PageWrapper>
  );
}

// ── Application MCQ detail (single 1-mark question with 5-block pedagogy) ──
function ApplicationMcqDetail({ q, isHi, goBack }) {
  const [picked, setPicked] = useState(null);
  const [step, setStep] = useState(0);
  const answered = picked !== null;
  const blocks = buildBlocks(q, isHi);
  const visibleBlocks = blocks.slice(0, step);

  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <DetailHeader q={q} isHi={isHi} marksLabel={isHi ? "ANK" : "MARK"} />

      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "16px 18px", marginBottom: 12,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Prashn" : "Question"}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#0F1B33", lineHeight: 1.5, whiteSpace: "pre-line" }}>
          {q.question}
        </div>
      </div>

      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "16px 18px", marginBottom: 14,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Vikalp chuno" : "Pick an option"}</div>
        <OptionsList
          options={q.options}
          correct={q.correct}
          picked={picked}
          onPick={(i) => { if (!answered) { setPicked(i); setStep(1); } }}
          answered={answered}
        />
      </div>

      <AnimatePresence>
        {visibleBlocks.map((b) => (
          <motion.div key={b.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <PedagogyBlock label={b.label} accent={b.accent || "terracotta"}>{b.content}</PedagogyBlock>
          </motion.div>
        ))}
      </AnimatePresence>

      <ContinueOrComplete answered={answered} step={step} total={blocks.length} onContinue={() => setStep(step + 1)} isHi={isHi} />

      <div style={{ height: 40 }} />
    </PageWrapper>
  );
}

// ── Case study detail ──
function CaseStudyDetail({ q, isHi, goBack }) {
  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <DetailHeader q={q} isHi={isHi} marksLabel={isHi ? "ANK" : "MARKS"} />
      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "16px 18px", marginBottom: 14,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Sthiti padho" : "Read the scenario"}</div>
        <div style={{ fontSize: 14, color: "#0F1B33", lineHeight: 1.6, whiteSpace: "pre-line" }}>{q.passage}</div>
      </div>
      {q.subQuestions.map((sq, i) => (
        <SubQuestionCard key={i} index={i + 1} sq={sq} isHi={isHi} />
      ))}
      <div style={{ height: 40 }} />
    </PageWrapper>
  );
}

// ── Detail header (shared by case study + assertion reason) ──
function DetailHeader({ q, isHi, marksLabel }) {
  return (
    <div style={{
      background: `linear-gradient(135deg, ${TERRACOTTA.heroStart}, ${TERRACOTTA.heroEnd})`,
      borderRadius: 14, padding: "16px 18px", marginBottom: 14,
      boxShadow: "0 8px 22px rgba(181,73,28,0.22)",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800,
        color: "rgba(255,255,255,0.88)", letterSpacing: "0.18em",
        textTransform: "uppercase", marginBottom: 6,
      }}>
        {q.section?.toUpperCase()} · {q.marks} {marksLabel}
      </div>
      <div style={{
        fontSize: 19, fontWeight: 800, color: "#FFFFFF",
        lineHeight: 1.25, letterSpacing: "-0.01em",
      }}>
        {q.title}
      </div>
    </div>
  );
}

// ── Sub-question card with INCREMENTAL solution reveal (step-through) ──
function SubQuestionCard({ index, sq, isHi }) {
  const [picked, setPicked] = useState(null);
  const [step, setStep] = useState(0);
  const answered = picked !== null;
  const blocks = buildBlocks(sq, isHi);
  const visibleBlocks = blocks.slice(0, step);

  return (
    <div style={{
      background: TERRACOTTA.cardBg, borderRadius: 14, padding: "16px 18px", marginBottom: 12,
      borderTop: "1px solid rgba(15,27,51,0.08)",
      borderRight: "1px solid rgba(15,27,51,0.08)",
      borderBottom: "1px solid rgba(15,27,51,0.08)",
      borderLeft: `4px solid ${TERRACOTTA.primary}`,
    }}>
      <div style={sectionLabelStyle}>{isHi ? "Prashn" : "Question"} {index}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#0F1B33", lineHeight: 1.45, marginBottom: 12, whiteSpace: "pre-line" }}>
        {sq.question}
      </div>
      <OptionsList options={sq.options} correct={sq.correct} picked={picked}
        onPick={(i) => { if (!answered) { setPicked(i); setStep(1); } }} answered={answered} />
      <AnimatePresence>
        {visibleBlocks.map((b) => (
          <motion.div key={b.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <PedagogyBlock label={b.label} accent={b.accent || "terracotta"}>{b.content}</PedagogyBlock>
          </motion.div>
        ))}
      </AnimatePresence>
      <ContinueOrComplete answered={answered} step={step} total={blocks.length} onContinue={() => setStep(step + 1)} isHi={isHi} />
    </div>
  );
}

// ── Assertion-Reason detail ──
function AssertionReasonDetail({ q, isHi, goBack }) {
  const [picked, setPicked] = useState(null);
  const [step, setStep] = useState(0);
  const answered = picked !== null;
  const blocks = buildBlocks(q, isHi);
  const visibleBlocks = blocks.slice(0, step);

  return (
    <PageWrapper>
      <div style={backBtnStyle} onClick={goBack}>← {isHi ? "Peeche" : "Back"}</div>
      <DetailHeader q={q} isHi={isHi} marksLabel={isHi ? "ANK" : "MARK"} />

      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "14px 16px", marginBottom: 10,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Kathan" : "Assertion (A)"}</div>
        <div style={{ fontSize: 14, color: "#0F1B33", lineHeight: 1.5, whiteSpace: "pre-line" }}>{q.assertion}</div>
      </div>
      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "14px 16px", marginBottom: 14,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Karan" : "Reason (R)"}</div>
        <div style={{ fontSize: 14, color: "#0F1B33", lineHeight: 1.5, whiteSpace: "pre-line" }}>{q.reason}</div>
      </div>

      <div style={{
        background: TERRACOTTA.cardBg, borderRadius: 14, padding: "16px 18px", marginBottom: 14,
        borderTop: "1px solid rgba(15,27,51,0.08)",
        borderRight: "1px solid rgba(15,27,51,0.08)",
        borderBottom: "1px solid rgba(15,27,51,0.08)",
        borderLeft: `4px solid ${TERRACOTTA.primary}`,
      }}>
        <div style={sectionLabelStyle}>{isHi ? "Vikalp chuno" : "Pick an option"}</div>
        <OptionsList options={q.options} correct={q.correct} picked={picked}
          onPick={(i) => { if (!answered) { setPicked(i); setStep(1); } }}
          answered={answered} alignTop />
      </div>

      <AnimatePresence>
        {visibleBlocks.map((b) => (
          <motion.div key={b.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <PedagogyBlock label={b.label} accent={b.accent || "terracotta"}>{b.content}</PedagogyBlock>
          </motion.div>
        ))}
      </AnimatePresence>

      <ContinueOrComplete answered={answered} step={step} total={blocks.length} onContinue={() => setStep(step + 1)} isHi={isHi} />

      <div style={{ height: 40 }} />
    </PageWrapper>
  );
}

// ── Helpers ──
function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

// Build ordered list of pedagogy blocks for a question/sub-question.
function buildBlocks(q, isHi) {
  const blocks = [];
  if (q.patternRecognition) {
    blocks.push({
      key: "pattern",
      label: isHi ? "Pattern pehchaano" : "Spot the pattern",
      content: <BlockText text={q.patternRecognition} />,
      accent: "terracotta",
    });
  }
  if (q.subGoals && q.subGoals.length > 0) {
    blocks.push({
      key: "subgoals",
      label: isHi ? "Step-by-step" : "Step-by-step",
      content: <BlockOL items={q.subGoals} />,
      accent: "terracotta",
    });
  }
  if (q.transferRule) {
    blocks.push({
      key: "transfer",
      label: isHi ? "Agli baar yaad rakho" : "Take it forward",
      content: <BlockText text={q.transferRule} />,
      accent: "green",
    });
  }
  if (q.similarProblem) {
    blocks.push({
      key: "similar",
      label: isHi ? "Yeh similar try karo" : "Try this similar one",
      content: <SimilarBlock sp={q.similarProblem} isHi={isHi} />,
      accent: "blue",
    });
  }
  if (q.trapAnalysis) {
    blocks.push({
      key: "trap",
      label: isHi ? "Aam galti" : "The common trap",
      content: <BlockText text={q.trapAnalysis} />,
      accent: "marigold",
    });
  }
  return blocks;
}

function OptionsList({ options, correct, picked, onPick, answered, alignTop }) {
  return (
    <div>
      {options.map((opt, i) => {
        const isCorrect = i === correct;
        const isPicked = i === picked;
        const showCorrect = answered && isCorrect;
        const showWrong = answered && isPicked && !isCorrect;
        let bg = "transparent";
        let border = "1px solid rgba(15,27,51,0.14)";
        let circleBg = TERRACOTTA.primary;
        let circleText = String.fromCharCode(65 + i);
        if (showCorrect) {
          bg = "rgba(26,122,82,0.10)";
          border = "2px solid #1A7A52";
          circleBg = "#1A7A52";
          circleText = "✓";
        } else if (showWrong) {
          bg = "rgba(220,53,69,0.10)";
          border = "2px solid #DC3545";
          circleBg = "#DC3545";
          circleText = "✗";
        }
        return (
          <div key={i} onClick={() => onPick(i)} style={{
            display: "flex",
            alignItems: alignTop ? "flex-start" : "center",
            gap: 10, padding: "10px 12px",
            borderRadius: 10, marginBottom: 6,
            border, background: bg,
            cursor: answered ? "default" : "pointer",
            transition: "all 0.15s",
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: circleBg, color: "#FFFFFF",
              fontWeight: 700, fontSize: 12,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginTop: alignTop ? 2 : 0,
            }}>
              {circleText}
            </div>
            <div style={{ fontSize: 13, color: "#0F1B33", flex: 1, lineHeight: 1.45 }}>{opt}</div>
          </div>
        );
      })}
    </div>
  );
}

function PedagogyBlock({ label, accent = "terracotta", children }) {
  const c = ACCENT[accent];
  return (
    <div style={{
      background: c.bg,
      borderLeft: `4px solid ${c.border}`,
      padding: "12px 14px",
      borderRadius: 8,
      marginBottom: 8,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 800, letterSpacing: "0.12em",
        color: c.labelColor, textTransform: "uppercase", marginBottom: 6,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ContinueOrComplete({ answered, step, total, onContinue, isHi }) {
  if (!answered) return null;
  if (step < total) {
    return (
      <div onClick={onContinue} style={continueBtnStyle}>
        {isHi ? "Aage badho →" : "Continue →"}
      </div>
    );
  }
  return (
    <div style={{
      marginTop: 8, padding: "10px 12px", textAlign: "center",
      fontSize: 12, fontWeight: 700, color: TERRACOTTA.primary,
      background: TERRACOTTA.tint, borderRadius: 8, letterSpacing: "0.06em",
      textTransform: "uppercase",
    }}>
      {isHi ? "Hal poora hua" : "Solution complete"}
    </div>
  );
}

function BlockText({ text }) {
  return <div style={{ fontSize: 13, color: "#0F1B33", lineHeight: 1.55, whiteSpace: "pre-line" }}>{text}</div>;
}

function BlockOL({ items }) {
  return (
    <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#0F1B33", lineHeight: 1.55 }}>
      {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
    </ol>
  );
}

function SimilarBlock({ sp, isHi }) {
  return (
    <>
      <div style={{ fontSize: 13, color: "#0F1B33", lineHeight: 1.55, marginBottom: 8 }}>
        <b>{isHi ? "Sthiti: " : "Scenario: "}</b>{sp.scenario}
      </div>
      {sp.hint && (
        <div style={{
          fontSize: 12, color: "#42506F", lineHeight: 1.5,
          paddingTop: 6, borderTop: "1px dashed rgba(46,158,206,0.30)",
        }}>
          <b>{isHi ? "Hint: " : "Hint: "}</b>{sp.hint}
        </div>
      )}
    </>
  );
}
